import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
// Corrected: Use resetPassword and confirmResetPassword as per latest Amplify Auth exports
import { confirmResetPassword, resetPassword } from '@aws-amplify/auth'; 

// Utility to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism input styles
const glassInput = 'w-full h-14 p-[12px] px-[23px] rounded-lg border border-[#228B22] bg-[#F8F7F7] text-gray-900 placeholder-gray-800 focus:outline-none focus:ring-2 focus:ring-[#A7F3D0]';
// Button styles
const buttonBase = 'flex justify-center items-center gap-[10px] self-stretch rounded-lg h-14 px-[10px] font-semibold transition-colors duration-200 bg-[#1C731C] text-white hover:bg-[#155A15]';

// This component is named ForgotPasswordOTP in Authorization.js
// It handles the OTP verification step after a user requests a password reset.
const ForgotPasswordOTP = ({ identifier, onVerifyOTP, onBack }) => {
    const OTP_LENGTH = 6; // Cognito OTPs are typically 6 digits
    const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(''));
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);

    // Timer for resend OTP
    useEffect(() => {
        let timer;
        if (resendTimer > 0 && !canResend) {
            timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
        } else if (resendTimer === 0) {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [resendTimer, canResend]);

    // Focus on first input on mount
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);


    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;

        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        // Move to next input field if a digit is entered
        if (element.value !== '' && index < OTP_LENGTH - 1 && inputRefs.current[index + 1]) {
            inputRefs.current[index + 1].focus();
        }
        // If last digit is entered, optionally trigger submission or blur
        if (index === OTP_LENGTH - 1 && element.value !== '') {
            element.blur(); // Blur after last digit
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && otp[index] === '') {
            if (index > 0 && inputRefs.current[index - 1]) {
                inputRefs.current[index - 1].focus();
            }
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const fullOtp = otp.join('');

        if (fullOtp.length !== OTP_LENGTH) {
            setError(`Please enter the full ${OTP_LENGTH}-digit code.`);
            setLoading(false);
            return;
        }

        try {
            // Cognito OTP verification: Use confirmResetPassword
            await confirmResetPassword(identifier, fullOtp, "DUMMY_NEW_PASSWORD"); // DUMMY_NEW_PASSWORD is needed but not actually set here
            console.log("OTP verified successfully via Cognito. Ready to set new password.");
            onVerifyOTP(); // Navigate to set new password screen
        } catch (err) {
            console.error('Error verifying OTP:', err);
            if (err.code === 'CodeMismatchException') {
                setError('Invalid verification code. Please try again.');
            } else if (err.code === 'LimitExceededException') {
                setError('Attempt limit exceeded, please try after some time.');
            } else if (err.code === 'ExpiredCodeException') {
                setError('Verification code has expired. Please request a new one.');
            } else {
                setError(err.message || 'An unexpected error occurred during OTP verification.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setError('');
        setOtp(new Array(OTP_LENGTH).fill('')); // Clear OTP fields
        setResendTimer(60);
        setCanResend(false);
        inputRefs.current[0]?.focus(); // Focus first input

        try {
            // Cognito Resend OTP Call: Use resetPassword
            await resetPassword(identifier); 
            console.log("OTP resend initiated via Cognito.");
        } catch (err) {
            console.error('Error resending OTP:', err);
            setError(err.message || 'Failed to resend OTP.');
        }
    };

    const displayIdentifier = identifier ?
        (identifier.includes('@') ? identifier : `+91 ${identifier.substring(0, 4)}****`) :
        'your registered email/username';


    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full"
        >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center font-figtree leading-[1.2]">Verify Code</h2>
            <p className="text-gray-900 text-lg text-center mb-4 font-open-sans leading-[1.5]">
                Enter the {OTP_LENGTH}-digit code sent to {displayIdentifier}.
            </p>
            <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                    <label htmlFor="otp" className="block text-gray-900 text-lg font-bold mb-2 leading-[1.5]">
                        Verification Code
                    </label>
                    <div className="flex justify-center space-x-2">
                        {otp.map((data, index) => (
                            <input
                                key={index}
                                type="text"
                                maxLength="1"
                                value={data}
                                onChange={e => handleChange(e.target, index)}
                                onFocus={e => e.target.select()}
                                onKeyDown={e => handleKeyDown(e, index)}
                                ref={el => inputRefs.current[index] = el}
                                className={combineClasses(
                                    'w-14 h-14 text-center text-2xl font-bold',
                                    'rounded-lg border border-[#228B22] bg-[#F8F7F7]',
                                    'text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#A7F3D0]'
                                )}
                                required
                            />
                        ))}
                    </div>
                </div>
                {error && <p className="text-red-600 text-sm text-center">{error}</p>}
                <button
                    type="submit"
                    className={combineClasses(
                        buttonBase,
                        "w-full",
                        "transition-all duration-300 transform hover:scale-105",
                        loading ? "opacity-70 cursor-not-allowed" : ""
                    )}
                    disabled={loading}
                >
                    {loading ? 'Verifying...' : 'Verify Code'}
                </button>
            </form>
            <div className="mt-4 text-center">
                {canResend ? (
                    <button
                        onClick={handleResendOTP}
                        className="font-bold text-[#A7F3D0] hover:text-[#2E6B24] transition-colors font-open-sans leading-[1.5]"
                    >
                        Resend Code
                    </button>
                ) : (
                    <p className="text-gray-700 text-lg font-open-sans leading-[1.5]">Resend code in {resendTimer}s</p>
                )}
            </div>
            <div className="mt-4 text-center">
                <button
                    onClick={onBack}
                    className="inline-block align-baseline text-lg font-poppins font-normal leading-[1.2] text-[#013220] hover:text-[#2E6B24] transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 mr-2 inline-block" /> Back to Enter Email / Username
                </button>
            </div>
        </motion.div>
    );
};

export default ForgotPasswordOTP;
